<template>
  <base-card no-header class="width-100">
    <template #body>
      <el-row>
        <el-col :span="5">
          <h3>
            {{ form.configurationName }}
          </h3>
          <h3 class="body-1 cursor-pointer mt-1" @click="openVersionModal">
            {{ $t('currentVersion') + ': ' + configuration.version }}
          </h3>
        </el-col>
        <el-col :span="15">
          <el-form ref="formRef" :model="form" :rules="formRules">
            <el-form-item
              class="mb-0"
              :label="$t('fileNameWithoutExtension')"
              prop="outputFileName"
            >
              <el-input
                v-model="form.outputFileName"
                size="small"
                @change="emitUpdate"
              />
            </el-form-item>
            <el-form-item
              :label="$t('apiConnectionName')"
              class="mb-0"
              prop="apiConnectionName"
            >
              <el-tooltip
                :disabled="
                  props.apiConnections.find(
                    c => Number(c.apiName) === Number(form.apiConnectionName)
                  )?.enabled
                "
                placement="top"
                :content="$t('disabledApiConnectionNoDataFetched')"
              >
                <el-select
                  v-model="form.apiConnectionName"
                  disabled
                  size="small"
                  filterable
                  @change="emitUpdate"
                >
                  <el-option
                    v-for="opt in apiNameOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  />
                </el-select>
              </el-tooltip>
            </el-form-item>
            <el-form-item
              :label="$t('executionTrigger')"
              class="mb-0"
              prop="executionTrigger"
            >
              <el-select
                v-model="form.executionTrigger"
                disabled
                size="small"
                filterable
                @change="emitUpdate"
              >
                <el-option
                  v-for="item in Object.keys(ExecutionTrigger)"
                  :key="item"
                  :label="$t(item)"
                  :value="ExecutionTrigger[item]"
                />
              </el-select>
            </el-form-item>
            <el-row>
              <el-col :span="8">
                <el-form-item
                  :label="$t('numberOfRetries')"
                  prop="numberOfRetries"
                >
                  <el-input-number
                    v-model="form.numberOfRetries"
                    :min="0"
                    :max="20"
                    @change="emitUpdate"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('retryDelayMilliseconds')"
                  prop="retryDelay"
                >
                  <el-input-number
                    v-model="form.retryDelay"
                    :min="0"
                    :max="30000"
                    :step="100"
                    @change="emitUpdate"
                  />
                </el-form-item>
              </el-col>
            </el-row>
            <el-divider>{{ $t('midBatchSettings') }}</el-divider>
            <el-form-item
              :label="$t('sqlNodeName')"
              class="mb-0"
              prop="sqlNodeName"
            >
              <el-tooltip placement="top" :content="$t('sqlNodeNameTooltip')">
                <el-select
                  v-model="form.sqlNodeName"
                  size="small"
                  filterable
                  @change="emitUpdate"
                >
                  <el-option
                    v-for="node in props.sqlNodes"
                    :key="node.name ?? ''"
                    :label="node.name ?? ''"
                    :value="node.name ?? ''"
                  />
                </el-select>
              </el-tooltip>
            </el-form-item>
            <el-form-item
              :label="$t('targetTableName')"
              class="mb-0"
              prop="targetTableName"
            >
              <el-input
                v-model="form.targetTableName"
                size="small"
                @change="emitUpdate"
              />
            </el-form-item>
          </el-form>
        </el-col>
        <el-col :span="4" class="actions-col-wrapper d-flex">
          <div class="actions-col d-flex">
            <img
              v-if="!disableDeleteAndSave"
              :src="require(`@/assets/images/icons/new/delete.svg`)"
              height="20"
              width="20"
              class="action-icon cursor-pointer"
              @click="deleteApiFileConfiguration"
            />
            <el-button
              v-if="!disableDeleteAndSave"
              type="primary"
              size="large"
              @click="saveItem"
            >
              {{ $t('save') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </template>
  </base-card>
  <gain-configuration-versions-modal
    v-model="showVersionModal"
    :configuration-versions="props.configurationHistory"
    @restore="handleRestore"
    @close="showVersionModal = false"
  />
</template>
<script lang="ts">
import BaseCard from '@/components/panels/BaseCard.vue';
import GainConfigurationVersionsModal from '@/views/admin/engagements/components/GainConfigurationVersionsModal.vue';
import { mustNotBeEmpty } from '@/utils/formValidators';
import { computed, onMounted, onUpdated, ref } from 'vue';
import {
  ElButton,
  ElCol,
  ElDivider,
  ElForm,
  ElFormItem,
  ElInput,
  ElInputNumber,
  ElMessage,
  ElOption,
  ElRow,
  ElSelect,
  ElTooltip
} from 'element-plus';
import {
  ApiName,
  ExecutionTrigger,
  ApiFileConfiguration,
  FileLogicType,
  ApiConnectionConfiguration
} from '@etp/etp-gain-bff-client/axios';
import { NodeModel } from '@etp/etp-nodemodelgit-client/axios';
import { MidBatchApiSettings } from '@/views/home/data-journey/filtering/models/MidBatchApiModels';
import i18n from '@/i18n';

export default {
  name: 'MidBatchApiFileConfigurationCard',
  components: {
    BaseCard,
    GainConfigurationVersionsModal,
    ElButton,
    ElCol,
    ElDivider,
    ElForm,
    ElFormItem,
    ElInput,
    ElInputNumber,
    ElOption,
    ElRow,
    ElSelect,
    ElTooltip
  },
  props: {
    configuration: {
      type: Object as () => ApiFileConfiguration,
      default: {} as ApiFileConfiguration,
      required: true
    },
    configurationHistory: {
      type: Array as () => Array<ApiFileConfiguration>,
      default: () => new Array<ApiFileConfiguration>()
    },
    disableDeleteAndSave: { type: Boolean, default: false },
    apiConnections: {
      type: Array as () => Array<ApiConnectionConfiguration>,
      default: () => new Array<ApiConnectionConfiguration>()
    },
    exitingOutputFileNames: {
      type: Array as () => Array<string>,
      default: () => new Array<string>()
    },
    sqlNodes: {
      type: Array as () => Array<NodeModel>,
      default: () => new Array<NodeModel>()
    }
  },
  emits: ['delete', 'update', 'save', 'fetch-history'],
  setup(props, { emit }) {
    const showVersionModal = ref(false);
    const formRef = ref<HTMLFormElement>();

    interface FormData {
      configurationName: string;
      outputFileName: string;
      apiConnectionName: ApiName;
      executionTrigger: ExecutionTrigger;
      numberOfRetries: number;
      retryDelay: number;
      sqlNodeName?: string;
      targetTableName: string;
    }

    const form = ref<FormData>({
      configurationName: '',
      outputFileName: '',
      apiConnectionName: ApiName.Haliburton,
      executionTrigger: ExecutionTrigger.MidBatch,
      numberOfRetries: 0,
      retryDelay: 0,
      sqlNodeName: '',
      targetTableName: ''
    });

    const emitUpdate = () => {
      if (form.value.outputFileName) {
        form.value.outputFileName = form.value.outputFileName.trim();
      }
      if (form.value.targetTableName) {
        form.value.targetTableName = form.value.targetTableName.trim();
      }

      const midBatchSettings: MidBatchApiSettings = {
        sqlNodeName: form.value.sqlNodeName || undefined,
        targetTableName: form.value.targetTableName
      };

      emit('update', {
        configurationName: form.value.configurationName,
        outputFileName: form.value.outputFileName,
        apiName: Number(form.value.apiConnectionName),
        executionTrigger: form.value.executionTrigger,
        numberOfRetries: form.value.numberOfRetries,
        retryDelay: form.value.retryDelay,
        apiSettings: JSON.stringify(midBatchSettings)
      } as ApiFileConfiguration);
    };

    const apiNameOptions = computed(() =>
      Object.entries(ApiName)
        .map(([key, value]) => ({ label: key, value }))
        .filter(entry =>
          props.apiConnections.map(c => c.apiName).includes(entry.value)
        )
    );

    const formRules = computed(() => ({
      outputFileName: [
        { validator: mustNotBeEmpty, trigger: 'blur' },
        {
          validator: localMustNotBeInArray,
          trigger: 'blur'
        }
      ],
      targetTableName: [{ validator: mustNotBeEmpty, trigger: 'blur' }]
    }));

    const localMustNotBeInArray = (rule: any, value: string, callback: any) => {
      if (
        props.exitingOutputFileNames.some(
          el => el.toLocaleLowerCase() == value.toLocaleLowerCase()
        )
      ) {
        callback(
          new Error(
            i18n.global.t('outputFileNameMustBeUniqueAcrossAllApiFileConfigs')
          )
        );
      } else callback();
    };

    const setFormValue = () => {
      let sqlNodeName = '';
      let targetTableName = '';

      if (props.configuration.apiSettings) {
        try {
          const settings: MidBatchApiSettings = JSON.parse(
            props.configuration.apiSettings
          );
          sqlNodeName = settings.sqlNodeName ?? '';
          targetTableName = settings.targetTableName ?? '';
        } catch {
          // Do nothing
        }
      }

      form.value = {
        configurationName: props.configuration.configurationName ?? '',
        outputFileName: props.configuration.outputFileName ?? '',
        apiConnectionName: props.configuration.apiName ?? ApiName.Haliburton,
        executionTrigger:
          props.configuration.executionTrigger ?? ExecutionTrigger.MidBatch,
        numberOfRetries: props.configuration.numberOfRetries ?? 0,
        retryDelay: props.configuration.retryDelay ?? 0,
        sqlNodeName: sqlNodeName,
        targetTableName: targetTableName
      };
    };

    const saveItem = async () => {
      let formValid = false;
      await formRef.value?.validate((valid: boolean) => {
        formValid = valid;
      });

      if (!formValid) {
        return;
      }

      if (!form.value.targetTableName) {
        ElMessage.error(i18n.global.t('targetTableNameRequired'));
        return;
      }

      emit('save');
    };

    const deleteApiFileConfiguration = () => {
      emit('delete');
    };

    const openVersionModal = () => {
      emit('fetch-history');
      showVersionModal.value = true;
    };

    const handleRestore = (input: ApiFileConfiguration) => {
      emit('update', input);
      showVersionModal.value = false;
    };

    onMounted(() => setFormValue());
    onUpdated(() => setFormValue());

    return {
      apiNameOptions,
      deleteApiFileConfiguration,
      emitUpdate,
      FileLogicType,
      form,
      formRef,
      formRules,
      props,
      ExecutionTrigger,
      openVersionModal,
      showVersionModal,
      handleRestore,
      saveItem
    };
  }
};
</script>
<style lang="scss" scoped>
.actions-col-wrapper {
  align-self: stretch;
}

.actions-col {
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  min-height: 100%;
}

:deep(.el-form-item.mb-0.is-error) {
  margin-bottom: 18px !important;
}
</style>
